import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, MessageCircle, Paperclip, Pin } from 'lucide-react';
import UserAvatar from '../UserAvatar';
import ProfilePopover from '../ProfilePopover';

function BoardTable({
  articles = [],
  totalArticles = 0,
  currentPage = 1,
  limit = 7,
  me,
  boardType,
  adminCol,
  setCheckedIds,
}) {
  const navigate = useNavigate();

  const handleCheckbox = (article) => {
    if (!setCheckedIds) return;
    if (article.isChecked) {
      setCheckedIds((prev) => prev.filter((id) => id !== article.id));
      article.isChecked = false;
    } else {
      setCheckedIds((prev) => [...prev, article.id]);
      article.isChecked = true;
    }
  };

  const handleTitleClick = (article) => {
    const isSecret = article.secretYn === 'Y';
    const isOwner = me?.id === Number(article.clientId);
    const isAdmin = me?.adminYn === 'Y';

    if (isSecret && !(isOwner || isAdmin)) {
      alert('비밀글입니다.');
      return;
    }

    navigate(`/${boardType}/View/${article.id}`);
  };

  const displayNumber = (index) =>
    totalArticles - (currentPage - 1) * limit - index;

  const isNew = (createdAt) =>
    new Date() - new Date(createdAt) < 24 * 60 * 60 * 1000;

  return (
    <div className="overflow-x-auto rounded-2xl border border-white/[0.08] shadow-sm">
      <table className="w-full table-auto text-sm">
        <thead>
          <tr className="bg-white/[0.03] border-b border-white/[0.08]">
            {!adminCol && (
              <th className="px-3 py-3 font-semibold text-slate-500 text-xs uppercase tracking-wide text-center w-12">
                관리
              </th>
            )}
            <th className="px-3 py-3 font-semibold text-slate-500 text-xs uppercase tracking-wide text-center w-16">
              번호
            </th>
            <th className="px-3 py-3 font-semibold text-slate-500 text-xs uppercase tracking-wide text-center hidden sm:table-cell w-24">
              카테고리
            </th>
            <th className="px-3 py-3 font-semibold text-slate-500 text-xs uppercase tracking-wide text-left">
              제목
            </th>
            <th className="px-3 py-3 font-semibold text-slate-500 text-xs uppercase tracking-wide text-center w-24">
              작성자
            </th>
            <th className="px-3 py-3 font-semibold text-slate-500 text-xs uppercase tracking-wide text-center hidden sm:table-cell w-28">
              작성일
            </th>
          </tr>
        </thead>
        <tbody className="bg-[#182334] divide-y divide-white/[0.05]">
          {articles.length === 0 ? (
            <tr>
              <td colSpan={6} className="py-16 text-center">
                <div className="flex flex-col items-center gap-2">
                  <MessageCircle size={32} className="text-slate-600" />
                  <p className="text-slate-500 text-sm">게시글이 없습니다.</p>
                </div>
              </td>
            </tr>
          ) : (
            articles.map((article, index) => (
              <tr
                key={article.id}
                className={`transition-colors cursor-pointer ${
                  article.isNotice
                    ? 'bg-sky-500/[0.06] hover:bg-sky-500/[0.1]'
                    : 'hover:bg-white/[0.03]'
                }`}
                onClick={() => handleTitleClick(article)}>
                {!adminCol && (
                  <td
                    className="px-3 py-3 text-center"
                    onClick={(e) => e.stopPropagation()}>
                    {me?.nameKr === article.writer && (
                      <input
                        type="checkbox"
                        checked={article.isChecked || false}
                        onChange={() => handleCheckbox(article)}
                        className="w-4 h-4 rounded border-white/20 bg-white/5 text-sky-500 focus:ring-sky-500/30"
                      />
                    )}
                  </td>
                )}
                <td className="px-3 py-3 text-center text-slate-500 text-xs">
                  {article.isNotice ? (
                    <span className="inline-flex items-center gap-1 text-sky-400 font-semibold">
                      <Pin size={12} />
                    </span>
                  ) : (
                    displayNumber(index)
                  )}
                </td>
                <td className="px-3 py-3 text-center hidden sm:table-cell">
                  <span
                    className={`inline-block rounded-full border px-2 py-0.5 text-[11px] font-medium ${
                      article.categoryId === '1'
                        ? 'border-sky-500/20 bg-sky-500/10 text-sky-400'
                        : 'border-pink-500/20 bg-pink-500/10 text-pink-400'
                    }`}>
                    {article.categoryName}
                  </span>
                </td>
                <td className="px-4 py-3 text-left">
                  <div className="flex items-center gap-1.5 min-w-0">
                    {article.secretYn === 'Y' && (
                      <Lock size={13} className="text-slate-500 shrink-0" />
                    )}
                    <span
                      className={`truncate text-sm ${
                        article.isNotice
                          ? 'font-semibold text-sky-300'
                          : 'text-slate-200 hover:text-white'
                      }`}>
                      {article.title}
                    </span>
                    {article.commentCount > 0 && (
                      <span className="inline-flex items-center gap-0.5 text-[11px] text-sky-400 shrink-0 ml-1">
                        <MessageCircle size={11} />
                        {article.commentCount}
                      </span>
                    )}
                    {article.fileCount > 0 && (
                      <Paperclip size={11} className="text-slate-500 shrink-0 ml-0.5" />
                    )}
                    {isNew(article.createdAt) && (
                      <span className="shrink-0 px-1.5 py-0.5 rounded text-[10px] font-bold bg-rose-500/20 text-rose-400 ml-1">
                        N
                      </span>
                    )}
                  </div>
                </td>
                <td
                  className="px-3 py-3 text-center"
                  onClick={(e) => e.stopPropagation()}>
                  {(() => {
                    const a = article.client
                      ? { userType: 'client', userId: article.client.id, name: article.client.nickName || article.client.name, profileImage: article.client.profileImage, adminYn: article.client.adminYn }
                      : article.member
                      ? { userType: 'member', userId: article.member.id, name: article.member.nickName || article.member.name, profileImage: article.member.profileImage, adminYn: 'N' }
                      : null;
                    if (!a) return null;
                    return (
                      <ProfilePopover user={a}>
                        <UserAvatar
                          name={a.name}
                          profileImage={a.profileImage}
                          userType={a.userType}
                          adminYn={a.adminYn}
                          size={20}
                          showRole={true}
                        />
                        <span className="text-slate-400 text-xs truncate max-w-[80px] hover:text-sky-400 transition-colors">
                          {a.name}
                        </span>
                      </ProfilePopover>
                    );
                  })()}
                </td>
                <td className="px-3 py-3 text-center text-slate-500 text-xs hidden sm:table-cell">
                  {new Date(article.createdAt).toLocaleDateString('ko-KR')}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

export default BoardTable;
